import React from 'react';
import { kebabCase } from 'change-case';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Preview as ContactPreview } from '@/components/pages/Subscription/Presentation/Preview/ContactPreview/Preview';
import { Contact, summarize } from '@/types/contact';
import { Label } from '@/components/shared/Label';
import { Dropdown } from '@/components/shared/Dropdown';

import * as styles from './ContactView.module.scss';
import { AddContactButton } from './AddContactButton';

type Props = {
  contacts: Contact[];
  error?: string;
  id: string;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  target?: 'billingContactId' | 'contactId';
  onChange: (contactId: number | undefined, touched: boolean) => void;
  value?: number;
  isContactExist: boolean;
  setModalMode?: React.Dispatch<React.SetStateAction<'new' | 'edit'>>;
  setContactToEdit?: React.Dispatch<React.SetStateAction<Contact | undefined>>;
};

export const ContactView: React.FC<Props> = ({
  contacts,
  error = '',
  id,
  setIsOpen,
  target,
  onChange,
  value,
  isContactExist,
  setModalMode,
  setContactToEdit,
}) => {
  const { t } = useTranslation();
  const addLabel =
    target === 'contactId'
      ? t('ui.subscription.contact.add')
      : t('ui.subscription.contact.addBillingDestination');

  const currentContact =
    contacts.find((contact) => contact.id === value) ||
    contacts[contacts.length - 1];

  return (
    <>
      {isContactExist && (
        <Dropdown
          text={summarize(currentContact, '　')}
          dataTest={kebabCase(id)}
          buttonClassNames={styles.toggleButton}
          menuWrapperClassNames={styles.menuWrapper}
          menuNode={
            <>
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  className={classNames(
                    styles.menuItem,
                    contact.id === value ? 'active' : '',
                  )}
                  onClick={() => onChange(contact.id, true)}
                >
                  {summarize(contact, '　')}
                </div>
              ))}
              <div
                key="add"
                className={classNames(styles.menuItem, styles.addContact)}
                onClick={() => {
                  setIsOpen(true);
                  if (setModalMode !== undefined) setModalMode('new');
                }}
              >
                {addLabel}
              </div>
            </>
          }
        />
      )}
      {error && <Label>{error}</Label>}

      {isContactExist && currentContact.id > 0 && (
        <ContactPreview
          contact={currentContact}
          setIsOpen={setIsOpen}
          setModalMode={setModalMode}
          setContactToEdit={setContactToEdit}
        />
      )}

      {!isContactExist && (
        <AddContactButton
          text={addLabel}
          onClick={() => {
            setIsOpen(true);
          }}
        />
      )}
    </>
  );
};
